import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import useBranch from '../../hooks/useBranch'; // Hook für Branches
import useVoucher from '../../hooks/useVoucher'; // Hook für Voucher
import LockIconWrapper from '../components/LockIconWrapper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const VoucherCheck = () => {
  const [voucherId, setVoucherId] = useState('');
  const [pin, setPin] = useState('');
  const [pinVisible, setPinVisible] = useState(false);
  const [pinRequired, setPinRequired] = useState(null);
  const [status, setStatus] = useState('');
  const [balance, setBalance] = useState(null);
  const [isReadonly, setIsReadonly] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [branch, setBranch] = useState('');
  const [amount, setAmount] = useState('');
  const [redeemFor, setRedeemFor] = useState('');
  // Entertainments the voucher is restricted to ('' selection = none chosen yet).
  const [restrictedEntertainments, setRestrictedEntertainments] = useState([]);
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  // Usage scope of the voucher ('all' | 'bookingOnly') — bookingOnly cannot be enforced
  // at the counter, so staff get a prominent hint before entering an amount.
  const [usageScope, setUsageScope] = useState('all');

  const { getBranches } = useBranch();
  const { resolveVoucher, checkVoucher, redeemVoucher } = useVoucher();

  useEffect(() => {
    const fetchBranches = async () => {
      const branches = await getBranches();
      const shownBranches = branches.map(branch => ({
        label: branch.locationName,
        id: branch._id,
      }));
      setBranchOptions(shownBranches);
      setBranch(shownBranches[0]?.id || '');
    };
    fetchBranches();
  }, []);

  const resetForm = () => {
    setVoucherId('');
    setPin('');
    setPinVisible(false);
    setPinRequired(null);
    setStatus('');
    setBalance(null);
    setIsReadonly(false);
    setAmount('');
    setRedeemFor('');
    setRestrictedEntertainments([]);
    setSelectedEntertainmentId('');
    setUsageScope('all');
  };

  const handleVoucherIdChange = event => {
    const inputValue = event.target.value.toUpperCase();
    setVoucherId(inputValue);
    setPin('');
    setPinVisible(false);
    setPinRequired(null);
  };

  const handleVoucherIdKeyDown = event => {
    if (event.key !== 'Enter' || isReadonly) return;
    event.preventDefault();
    if (!voucherId.trim()) return;
    if (voucherId.includes('-')) {
      const [id, scannedPin] = voucherId.split('-', 2);
      setVoucherId(id);
      setPin(scannedPin || '');
      void handleCheckVoucher(id, scannedPin || '');
      return;
    }
    void handleCheckVoucher();
  };

  const handlePinKeyDown = event => {
    if (event.key !== 'Enter' || isReadonly) return;
    if (isCheckButtonDisabled) return;
    event.preventDefault();
    void handleCheckVoucher();
  };

  const handlePinChange = event => {
    setPin(event.target.value);
  };

  const handleAmountChange = event => {
    const input = event.target.value;
    if (/^\d*\.?\,?\d*$/.test(input)) {
      setAmount(input);
    }
  };

  const handleRedeem = async () => {
    try {
      if (restrictedEntertainments.length > 0 && !selectedEntertainmentId) {
        toast.error('Bitte das Entertainment auswählen, für das der Gutschein gilt.');
        return;
      }
      const toastMessage =
        'Die Einlöseanfrage für den Gutschein wurde gespeichert und kann später in den offenen Anfragen bestätigt werden';
      const successToastMessage = 'Gutschein erfolgreich eingelöst!';
      const branchLabel = branchOptions.find(b => b.id === branch)?.label || branch;
      const entertainmentLabel = restrictedEntertainments.find(
        e => String(e._id) === String(selectedEntertainmentId)
      )?.longName;
      const displayInfo = {
        Typ: 'Gutschein einlösen',
        Gutschein: voucherId,
        Betrag: `${amount.replace(',', '.')} €`,
        Branch: branchLabel,
        ...(entertainmentLabel ? { Entertainment: entertainmentLabel } : {}),
        'Eingelöst für': redeemFor || '–',
      };
      const onStored = () => {
        resetForm();
      };

      const redeemResult = await redeemVoucher(
        voucherId,
        {
          pin: pinRequired ? pin : undefined,
          amount: parseFloat(amount.replace(',', '.')),
          comment: redeemFor,
          branchId: branch,
          entertainmentId: selectedEntertainmentId || undefined,
        },
        toastMessage,
        onStored,
        displayInfo,
        null,
        successToastMessage
      );
      if (redeemResult === null) {
        return;
      }
      if (redeemResult?.success) {
        toast.success(successToastMessage);
        resetForm();
        return;
      }
      toast.error('Fehler beim Einlösen des Gutscheins.');
    } catch (error) {
      if (error?.code === 'VOUCHER_TOO_MANY_ATTEMPTS') {
        toast.error('Zu viele Fehlversuche. Bitte kurz warten.');
        return;
      }
      if (error?.code === 'VOUCHER_BRANCH_NOT_ALLOWED') {
        const allowed = Array.isArray(error?.data?.data?.allowedBranchIds)
          ? error.data.data.allowedBranchIds
          : Array.isArray(error?.data?.allowedBranchIds)
            ? error.data.allowedBranchIds
            : [];
        const labels = allowed
          .map(id => branchOptions.find(b => b.id === id)?.label)
          .filter(Boolean);
        if (labels.length > 0) {
          toast.error(`Dieser Gutschein kann nur in ${labels.join(', ')} eingelöst werden.`);
        } else {
          toast.error('Dieser Gutschein kann an diesem Standort nicht eingelöst werden.');
        }
        return;
      }
      if (error?.code === 'VOUCHER_ENTERTAINMENT_NOT_ALLOWED') {
        const allowed = Array.isArray(error?.data?.data?.allowedEntertainmentIds)
          ? error.data.data.allowedEntertainmentIds
          : Array.isArray(error?.data?.allowedEntertainmentIds)
            ? error.data.allowedEntertainmentIds
            : [];
        const labels = allowed
          .map(id => restrictedEntertainments.find(e => String(e._id) === String(id))?.longName)
          .filter(Boolean);
        if (labels.length > 0) {
          toast.error(`Dieser Gutschein kann nur für ${labels.join(', ')} eingelöst werden.`);
        } else {
          toast.error('Dieser Gutschein kann für dieses Entertainment nicht eingelöst werden.');
        }
        return;
      }
      if (error?.code === 'VOUCHER_PIN_REQUIRED') {
        toast.error('Für diesen Gutschein wird eine PIN benötigt.');
        return;
      }
      if (error?.code === 'VOUCHER_PIN_INVALID') {
        toast.error('Die PIN ist ungültig.');
        return;
      }
      if (error?.code === 'VOUCHER_NOT_FOUND') {
        // This screen only knows value vouchers, so the message says where a
        // discount code belongs instead of reading like the code were invalid.
        toast.error(
          'Gutschein nicht gefunden. Mitarbeiter- und Aktions-Rabattcodes werden hier nicht geprüft, sondern direkt in der Buchung eingelöst.'
        );
        return;
      }
      if (error?.code === 'VOUCHER_EXPIRED') {
        toast.error('Dieser Gutschein ist abgelaufen und kann nicht mehr eingelöst werden.');
        return;
      }
      if (error?.code === 'VOUCHER_NOT_ACTIVE') {
        toast.error('Dieser Gutschein ist nicht aktiv und kann nicht eingelöst werden.');
        return;
      }
      toast.error('Fehler beim Einlösen des Gutscheins.');
    }
  };

  const handleCheckVoucher = async (id = voucherId, voucherPin = pin) => {
    try {
      const resolved = await resolveVoucher(id);
      const requiresPin = Boolean(resolved?.requiresPin);
      setPinRequired(requiresPin);
      setPinVisible(requiresPin);
      if (requiresPin && !voucherPin) {
        toast.info('Bitte PIN eingeben');
        return;
      }
      const {
        status,
        balance,
        restrictedEntertainments: restrictedList,
        usageScope: checkedUsageScope,
      } = await checkVoucher(id, requiresPin ? voucherPin : undefined);
      setStatus(status);
      setBalance(balance);
      setUsageScope(checkedUsageScope === 'bookingOnly' ? 'bookingOnly' : 'all');
      const entertainments = Array.isArray(restrictedList) ? restrictedList : [];
      setRestrictedEntertainments(entertainments);
      // Auto-select when the voucher is restricted to a single entertainment.
      setSelectedEntertainmentId(entertainments.length === 1 ? entertainments[0]._id : '');
      setIsReadonly(true);
    } catch (error) {
      if (error?.code === 'VOUCHER_TOO_MANY_ATTEMPTS') {
        toast.error('Zu viele Fehlversuche. Bitte kurz warten.');
        return;
      }
      if (error?.code === 'VOUCHER_PIN_REQUIRED') {
        toast.error('Für diesen Gutschein wird eine PIN benötigt.');
        setStatus('');
        setBalance(null);
        setIsReadonly(false);
        return;
      }
      if (error?.code === 'VOUCHER_PIN_INVALID') {
        toast.error('Die PIN ist ungültig.');
        setPin('');
        setStatus('');
        setBalance(null);
        setIsReadonly(false);
        return;
      }
      if (error?.code === 'VOUCHER_NOT_FOUND') {
        toast.error(
          'Gutschein nicht gefunden. Mitarbeiter- und Aktions-Rabattcodes werden hier nicht geprüft, sondern direkt in der Buchung eingelöst.'
        );
        setStatus('');
        setBalance(null);
        setIsReadonly(false);
        return;
      }
      if (error?.code === 'VOUCHER_EXPIRED') {
        toast.error('Dieser Gutschein ist abgelaufen und kann nicht mehr eingelöst werden.');
        setStatus('expired');
        setBalance(null);
        setIsReadonly(false);
        return;
      }
      if (error?.code === 'VOUCHER_NOT_ACTIVE') {
        toast.error('Dieser Gutschein ist nicht aktiv und kann nicht eingelöst werden.');
        setStatus('');
        setBalance(null);
        setIsReadonly(false);
        return;
      }
      toast.error('Fehler beim Prüfen des Gutscheins.');
      resetForm();
    }
  };

  const handleBranchChange = event => {
    setBranch(event.target.value);
  };

  const translateStatus = {
    active: 'aktiv',
    used: 'vollständig eingelöst',
    locked: 'gesperrt (Zahlungsproblem)',
    waitingForPayment: 'unbezahlt',
    expired: 'abgelaufen',
  };

  const isRedeemButtonDisabled =
    !amount ||
    !redeemFor ||
    !branch ||
    parseFloat(amount.replace(',', '.')) <= 0 ||
    (restrictedEntertainments.length > 0 && !selectedEntertainmentId);

  const isCheckButtonDisabled = !voucherId || (pinRequired ? !pin : false);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, maxWidth: 400, margin: '0 auto', textAlign: 'center', pt: 3 }}>
      <TextField
        label='Gutscheincode'
        value={voucherId}
        onChange={handleVoucherIdChange}
        onKeyDown={handleVoucherIdKeyDown}
        disabled={isReadonly}
        autoFocus
        fullWidth
        sx={{ mb: 2 }}
      />
      {pinVisible && (
        <TextField
          label='PIN'
          value={pin}
          onChange={handlePinChange}
          onKeyDown={handlePinKeyDown}
          disabled={isReadonly}
          fullWidth
          sx={{ mb: 2 }}
        />
      )}
      {!isReadonly && (
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleCheckVoucher()}
          disabled={isCheckButtonDisabled}
          fullWidth
        >
          Prüfen
        </Button>
      )}
      {isReadonly && (
        <>
          <Typography
            variant='h6'
            sx={{ mt: 2, color: status === 'active' ? 'success.main' : 'error.main' }}
          >
            Status: {translateStatus[status] || 'Unbekannt'}
          </Typography>
          {balance !== null && (
            <Typography variant='h6'>
              Guthaben: {balance.toFixed(2).toString().replace('.', ',')} €
            </Typography>
          )}
          {status === 'active' && (
            <>
              {usageScope === 'bookingOnly' && (
                <Alert severity='warning' sx={{ mt: 2, textAlign: 'left' }}>
                  Dieser Gutschein gilt <strong>nur für die gebuchte Leistung</strong> — nicht
                  für Speisen, Getränke oder sonstige Extras. Bitte nur den Anteil der
                  eigentlichen Leistung einlösen.
                </Alert>
              )}
              {branchOptions.length > 1 ? (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id='demo-simple-select-label'>Branch</InputLabel>

                  <Select value={branch} label='Branch' onChange={handleBranchChange} size='small'>
                    {branchOptions.map(br => (
                      <MenuItem key={br.id} value={br.id}>
                        {br.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                ''
              )}
              {restrictedEntertainments.length === 1 && (
                <Typography variant='body2' sx={{ mt: 2, color: 'warning.main' }}>
                  Nur einlösbar für: <strong>{restrictedEntertainments[0].longName}</strong>
                </Typography>
              )}
              {restrictedEntertainments.length > 1 && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id='voucher-entertainment-label'>Einlösen für Entertainment</InputLabel>
                  <Select
                    labelId='voucher-entertainment-label'
                    value={selectedEntertainmentId}
                    label='Einlösen für Entertainment'
                    onChange={event => setSelectedEntertainmentId(event.target.value)}
                    size='small'
                  >
                    {restrictedEntertainments.map(ent => (
                      <MenuItem key={ent._id} value={ent._id}>
                        {ent.longName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label='Betrag'
                value={amount}
                onChange={handleAmountChange}
                fullWidth
                sx={{ mt: 2 }}
                type='text'
              />
              <TextField
                label='Eingelöst für Produkt'
                value={redeemFor}
                onChange={event => setRedeemFor(event.target.value)}
                fullWidth
                sx={{ mt: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant='contained'
                  color='success'
                  onClick={handleRedeem}
                  disabled={isRedeemButtonDisabled}
                >
                  <LockIconWrapper right='voucher:redeem'>Bestätigen</LockIconWrapper>
                </Button>
                <Button variant='contained' color='error' onClick={resetForm}>
                  Abbrechen
                </Button>
              </Box>
            </>
          )}
          {status !== 'active' && (
            <Button
              variant='contained'
              color='success'
              onClick={resetForm}
              fullWidth
              sx={{ mt: 2 }}
            >
              OK
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default VoucherCheck;
